/*
 * Google Tag Manager container, europe only.
 *
 * Same reasoning as utils/googleAds.js, and for the same two reasons it is not the plain <script>
 * GTM's install page hands out:
 *
 *   - index.html is shared by both builds, so a static tag would also fire on ainovel.ru, which
 *     measures through Yandex.Metrika and has no Plotex container.
 *   - A container is a tag loader: whatever is configured in the GTM UI runs under it. That makes
 *     it "analytics" at the very least, and utils/consent.js applies the strictest jurisdiction to
 *     everyone — nothing non-essential before the banner is answered. Hard-coding it into <head>
 *     would load it on first paint, which is exactly what the Russian-tracker guard in index.html
 *     exists to prevent.
 *
 * Consent Mode keeps that honest once the container is loaded: utils/googleConsent.js has
 * already put the default on the dataLayer, so any Google tag configured inside the container
 * starts denied and is released only for the categories the visitor granted. Without it, gating
 * the loader on 'analytics' would still let a marketing tag in the container fire for someone who
 * refused marketing.
 *
 * The noscript <iframe> from the install snippet is deliberately omitted. It cannot be gated —
 * with scripting off there is no banner to answer and no way to honour a refusal — so it would be
 * the one unconditional tracker on the page. It also measures nothing here: the app is client-side
 * React and <noscript> already tells those visitors the site needs JavaScript.
 */

import { IS_EUROPE } from '../config/region';
import { allows, onConsentChange } from './consent';

const GTM_ID = 'GTM-NW8846CL';

let started = false;

const startContainer = () => {
  if (started) return;
  started = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);
};

/*
 * Returns an unsubscribe function, or undefined when there is nothing left to wait for.
 *
 * During prerendering nothing is stored and there is no banner to answer, so allows() is false and
 * the container is never injected — the shipped HTML a crawler reads stays free of it.
 */
export const initGoogleTagManager = () => {
  if (!IS_EUROPE) return undefined;
  if (allows('analytics')) {
    startContainer();
    return undefined;
  }
  return onConsentChange((consent) => {
    if (consent.analytics) startContainer();
  });
};
